import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useCurrentUser, isSuperAdminRole } from '../lib/auth'
import { formatDateTimeShort } from '../lib/dates'

export const Route = createFileRoute('/admin/null-losanir')({
  component: ZeroCollectionsPage,
})

const KIND_LABEL: Record<string, string> = {
  empty: 'Tóm tunna',
  noaccess: 'Komst ekki að',
  other: 'Annað',
  blank: 'Engin athugasemd',
}
const KIND_COLOR: Record<string, string> = {
  empty: '#5C8E3C',
  noaccess: '#CB6B50',
  other: '#5A6E78',
  blank: '#94A3B8',
}

function ZeroCollectionsPage() {
  const { data: me, isLoading: meLoading } = useCurrentUser()
  const qc = useQueryClient()
  const isSuper = isSuperAdminRole(me)

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'zero-collections'],
    queryFn: () => api.admin.zeroCollections.get(),
    enabled: isSuper,
  })

  const refresh = useMutation({
    mutationFn: () => api.admin.zeroCollections.refresh(),
    onSuccess: (res) => qc.setQueryData(['admin', 'zero-collections'], res),
  })

  const [kind, setKind] = useState<string>('')
  const [company, setCompany] = useState<string>('')

  const report = data?.report ?? refresh.data?.report ?? null

  const remarks = useMemo(() => {
    if (!report) return []
    return report.remarks.filter((r) =>
      (!kind || r.kind === kind) && (!company || r.company === company))
  }, [report, kind, company])

  if (meLoading) return <Shell><p className="text-[var(--muted)]">Hleð…</p></Shell>
  if (!isSuper) return (
    <Shell>
      <div className="rounded-xl bg-amber-50 border border-amber-200 p-6 text-amber-900">
        <h2 className="font-semibold">Enginn aðgangur</h2>
        <p className="mt-1 text-sm">Aðeins superadmin sér þessa skýrslu.</p>
      </div>
    </Shell>
  )

  return (
    <Shell>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="font-grotesk font-bold text-[28px] leading-tight text-[var(--fg)]">Núll-losanir</h1>
          <p className="mt-1 text-sm text-[var(--muted)]">
            Ferðir þar sem ekkert var sótt (0 kg), brotið niður á fyrirtæki — síðustu {report?.months ?? 6} mánuði.
            {report && <> Tímabil {report.fromMonth} – {report.toMonth}.</>}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {report && <span className="text-xs text-[var(--faint)]">Síðast reiknað: {formatDateTimeShort(report.generatedAt)}</span>}
          <button
            onClick={() => refresh.mutate()}
            disabled={refresh.isPending}
            className="rounded-lg bg-[var(--brand)] px-4 py-2 text-sm font-semibold text-[var(--on-brand)] hover:opacity-90 disabled:opacity-50"
          >
            {refresh.isPending ? 'Reikna…' : 'Uppfæra núna'}
          </button>
        </div>
      </div>

      {isLoading && <p className="text-[var(--muted)]">Hleð…</p>}

      {!isLoading && !report && (
        <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-6 text-sm text-[var(--muted)]">
          Engin skýrsla reiknuð enn. Smelltu á „Uppfæra núna" til að reikna hana (getur tekið smá tíma í fyrsta sinn).
        </div>
      )}

      {report && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <Tile label="Núll-losanir alls" value={report.totals.zero} sub={`${pct(report.totals.zero, report.totals.collections)} af öllum losunum`} color="#141414" />
            <Tile label="Tóm tunna" value={report.totals.empty} sub="bíll mætti, ekkert að sækja" color={KIND_COLOR.empty} />
            <Tile label="Komst ekki að" value={report.totals.noAccess} sub="lokað / bíll fyrir / framkvæmdir" color={KIND_COLOR.noaccess} />
            <Tile label="Annað / óskráð" value={report.totals.other + report.totals.blank} sub="annar texti eða engin athugasemd" color={KIND_COLOR.other} />
          </div>

          <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)] overflow-x-auto mb-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-[var(--faint)] border-b border-[var(--border)]">
                  <th className="px-4 py-2 font-medium">Fyrirtæki</th>
                  <th className="px-3 py-2 font-medium text-right">Núll-losanir</th>
                  <th className="px-3 py-2 font-medium text-right">Tóm tunna</th>
                  <th className="px-3 py-2 font-medium text-right">Komst ekki að</th>
                  <th className="px-3 py-2 font-medium text-right">Annað</th>
                  <th className="px-3 py-2 font-medium text-right">Engin ath.</th>
                  <th className="px-3 py-2 font-medium text-right">% af losunum</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border)]">
                {report.companies.map((c) => (
                  <tr key={c.id} className="hover:bg-[var(--surface-2)]">
                    <td className="px-4 py-2 text-[var(--fg)]">{c.name}</td>
                    <td className="px-3 py-2 text-right font-semibold text-[var(--fg)] tabular-nums">{c.zero.toLocaleString('is-IS')}</td>
                    <td className="px-3 py-2 text-right tabular-nums" style={{ color: KIND_COLOR.empty }}>{c.empty || '–'}</td>
                    <td className="px-3 py-2 text-right tabular-nums" style={{ color: KIND_COLOR.noaccess }}>{c.noAccess || '–'}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-[var(--muted)]">{c.other || '–'}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-[var(--faint)]">{c.blank || '–'}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-[var(--muted)]">{c.zeroPct.toLocaleString('is-IS')}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-4">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <h2 className="text-sm font-semibold text-[var(--fg)]">Athugasemdir bílstjóra ({remarks.length.toLocaleString('is-IS')})</h2>
              <div className="flex gap-2">
                <select value={company} onChange={(e) => setCompany(e.target.value)} className={sel}>
                  <option value="">Öll fyrirtæki</option>
                  {report.companies.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
                <select value={kind} onChange={(e) => setKind(e.target.value)} className={sel}>
                  <option value="">Allar ástæður</option>
                  <option value="empty">Tóm tunna</option>
                  <option value="noaccess">Komst ekki að</option>
                  <option value="other">Annað</option>
                  <option value="blank">Engin athugasemd</option>
                </select>
              </div>
            </div>
            <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-[var(--surface)]">
                  <tr className="text-left text-[11px] uppercase tracking-wide text-[var(--faint)] border-b border-[var(--border)]">
                    <th className="px-3 py-2 font-medium">Dags.</th>
                    <th className="px-3 py-2 font-medium">Fyrirtæki</th>
                    <th className="px-3 py-2 font-medium">Staður</th>
                    <th className="px-3 py-2 font-medium">Efni</th>
                    <th className="px-3 py-2 font-medium">Ástæða</th>
                    <th className="px-3 py-2 font-medium">Athugasemd</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border)]">
                  {remarks.map((r, i) => (
                    <tr key={i} className="hover:bg-[var(--surface-2)]">
                      <td className="px-3 py-1.5 whitespace-nowrap text-[var(--muted)] tabular-nums">{r.date}</td>
                      <td className="px-3 py-1.5 text-[var(--fg)]">{r.company}</td>
                      <td className="px-3 py-1.5 text-[var(--muted)]">{r.location}</td>
                      <td className="px-3 py-1.5 text-[var(--muted)]">{r.material}</td>
                      <td className="px-3 py-1.5 whitespace-nowrap">
                        <span className="rounded-full px-2 py-0.5 text-[10px] font-medium" style={{ background: KIND_COLOR[r.kind] + '22', color: KIND_COLOR[r.kind] }}>
                          {KIND_LABEL[r.kind] ?? r.kind}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-[var(--fg)]">{r.text || <span className="text-[var(--faint)]">—</span>}</td>
                    </tr>
                  ))}
                  {remarks.length === 0 && (
                    <tr><td colSpan={6} className="px-3 py-6 text-center text-[var(--faint)]">Engar athugasemdir passa við síur.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </Shell>
  )
}

const sel = 'rounded-lg border border-[var(--border)] bg-[var(--surface-2)] px-3 py-1.5 text-sm text-[var(--fg)] outline-none focus:border-[var(--brand)]'

function pct(part: number, whole: number): string {
  return whole > 0 ? `${((part / whole) * 100).toLocaleString('is-IS', { maximumFractionDigits: 1 })}%` : '0%'
}

function Tile({ label, value, sub, color }: { label: string; value: number; sub: string; color: string }) {
  return (
    <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-4 border-l-4" style={{ borderLeftColor: color }}>
      <p className="text-[11px] font-medium uppercase tracking-wide text-[var(--muted)]">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-[var(--fg)] tabular-nums">{value.toLocaleString('is-IS')}</p>
      <p className="mt-0.5 text-[11px] text-[var(--faint)]">{sub}</p>
    </div>
  )
}

function Shell({ children }: { children: React.ReactNode }) {
  return <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-8">{children}</div>
}
